import { create } from '@bufbuild/protobuf'
import { type ConnectError } from '@connectrpc/connect'
import { useQuery } from '@connectrpc/connect-query'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { getAreaWeatherForecast } from '@ebalance/proto/bffuser/v1/weather_forecast_service-WeatherForecastService_connectquery'
import {
  GetAreaWeatherForecastRequestSchema,
  type GetAreaWeatherForecastResponse,
} from '@ebalance/proto/bffuser/v1/weather_forecast_service_pb'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'
import type { UseQueryResult } from '@tanstack/react-query'

import { WEATHER_REFETCH_INTERVAL_MS } from '~/hooks/weather/refetchInterval'

/** useAreaWeatherForecast のパラメータ。 */
export type UseAreaWeatherForecastParams = {
  areaCode: AreaCode
  targetDateFrom: ProtoDate
  targetDateTo: ProtoDate
  /** area / 日付が未確定の間はクエリを無効化する (先行配線)。 */
  enabled?: boolean
}

/**
 * 電力エリアの気象予報 (BigQuery 由来 8 metric) を取得する。
 *
 * 気象 gRPC は「1 本で全メトリクスを返し全画面が使い回す」設計のため、
 * ページ横断の共有 hook (`src/hooks/weather/`) に置く。消費画面:
 * BalanceDemand (天気アイコン帯)。TODO(#2496): bg-region 受け皿画面でも consume する。
 *
 * 再取得は `WEATHER_REFETCH_INTERVAL_MS` (120 秒) の定期 polling (根拠は同定数の
 * JSDoc)。予報の発表自体は 1 日 3 回 (05/11/17 時 JST) だが、DPD 側の取込タイミングを
 * 画面側が知らないため、実績気象と同じ間隔で回して最新版へ差し替える。
 *
 * fail-safe: 天気帯は補助表示なので、BQ 未整備 (#2499) 等で取得失敗しても
 * error boundary (全画面エラー) に飛ばさない。global QueryClient の
 * `throwOnError: true` を本 query だけ `false` に上書きし、失敗時は
 * data undefined → 空帯降格でチャート本体の描画を妨げない。
 */
export function useAreaWeatherForecast(
  params: UseAreaWeatherForecastParams,
): UseQueryResult<GetAreaWeatherForecastResponse, ConnectError> {
  return useQuery(
    getAreaWeatherForecast,
    create(GetAreaWeatherForecastRequestSchema, {
      areaCode: params.areaCode,
      targetDateFrom: params.targetDateFrom,
      targetDateTo: params.targetDateTo,
    }),
    {
      enabled: params.enabled ?? true,
      throwOnError: false,
      refetchInterval: WEATHER_REFETCH_INTERVAL_MS,
    },
  )
}
